import { useId, useMemo, useCallback, useEffect, useState } from 'react';
import { useOnValueChangeEffect } from '../hooks/use-on-value-change-effect.js';
import { accept } from '../utils/event.js';
import { imod } from '../utils/math.js';
import { objectKeys } from '../utils/object.js';
import { getActiveTabs, focusTabInGroup, brand, firstActiveTab, getPanelId, getTriggerId } from './tabs-primitive.utils.js';

/**
 * Hook to manage state for a set of tabs.
 *
 * We return three values:
 * - `tabPropsMap`: Props to pass to `Tabs.Tab`, the component that you click on to switch tabs
 * - `tabPanelPropsMap`: Props to pass to `Tabs.TabPanel`, the component that contains tab content
 * - `tabManager`: An object that can be used to script the tab state
 */
function useTabs(tabIds, options = {}) {
    const { defaultActive, onChange } = options;
    const [activeTab, setActiveTab] = useState(() => typeof defaultActive === 'function'
        ? defaultActive()
        : (defaultActive ?? firstActiveTab(tabIds)));
    useOnValueChangeEffect((lastTab) => {
        onChange?.(activeTab, lastTab);
    }, activeTab);
    return useManagedTabs(tabIds, activeTab, setActiveTab, options);
}
/**
 * Hook to manage state for a set of tabs, when the tab state should be tied to external state.
 *
 * We return three values:
 * - `tabPropsMap`: Props to pass to `Tabs.Tab`, the component that you click on to switch tabs
 * - `tabPanelPropsMap`: Props to pass to `Tabs.TabPanel`, the component that contains tab content
 * - `tabManager`: An object that can be used to script the tab state.
 */
function useManagedTabs(tabIds, activeTab, setActiveTab, options) {
    const orientation = options?.orientation ?? 'horizontal';
    // We need an id to make our tab groups unique.
    const tabGroupId = useId();
    // Filter out tabs that are set as "false" and turn it into a list array
    const memoizedTabList = useMemo(() => getActiveTabs(tabIds), [tabIds]);
    const setAndFocusActiveTab = useCallback((tab, skipFocus) => {
        setActiveTab(tab);
        if (!skipFocus) {
            focusTabInGroup(tabGroupId, tab);
        }
    }, [setActiveTab, tabGroupId]);
    const manager = useMemo(() => ({
        activeTab,
        tabs: memoizedTabList,
        tabGroupId,
        setActiveTab: setAndFocusActiveTab,
        orientation,
    }), [activeTab, memoizedTabList, tabGroupId, setAndFocusActiveTab, orientation]);
    // There is a possibility that a visible tab will be hidden dynamically
    // Should this happen this function will fire and remove it and select the
    // first tab.
    useEffect(() => {
        if (!tabIds[activeTab]) {
            setAndFocusActiveTab(memoizedTabList[0]);
        }
    }, [tabIds, activeTab, memoizedTabList, setAndFocusActiveTab]);
    const onKeyDown = useCallback((e) => {
        // TODO: standardize this? why do we care about CapsLock
        if (e.getModifierState('Alt') ||
            e.getModifierState('Control') ||
            e.getModifierState('CapsLock')) {
            return;
        }
        const n = memoizedTabList.length;
        let nextIndex = memoizedTabList.indexOf(activeTab);
        switch (e.key) {
            case 'ArrowUp':
            case 'ArrowLeft':
                nextIndex -= 1;
                break;
            case 'ArrowDown':
            case 'ArrowRight':
                nextIndex += 1;
                break;
            case 'Home':
                nextIndex = 0;
                break;
            case 'End':
                nextIndex = n - 1;
                break;
            default:
                return;
        }
        accept(e);
        // Only loop on the first key press, otherwise stop at the ends, like MacOS's ⌘+Tab switcher.
        if (e.repeat && (nextIndex > n - 1 || nextIndex < 0))
            return;
        setAndFocusActiveTab(memoizedTabList[imod(nextIndex, n)]);
    }, [activeTab, memoizedTabList, setAndFocusActiveTab]);
    const tabPropsMap = useMemo(() => objectKeys(tabIds).reduce((acc, id) => {
        acc[id] = brand({
            id,
            display: tabIds[id],
            manager,
            triggerId: getTriggerId(tabGroupId, id),
            panelId: getPanelId(tabGroupId, id),
            onKeyDown,
            actionOnPointerDown: options?.actionOnPointerDown ?? true,
            recordingKey: options?.recordingKey ? `${options.recordingKey}.${id}` : undefined,
        });
        return acc;
    }, {}), [manager, tabGroupId, onKeyDown, tabIds, options?.actionOnPointerDown, options?.recordingKey]);
    const tabPanelPropsMap = useMemo(() => objectKeys(tabIds).reduce((acc, id) => {
        acc[id] = brand({
            id,
            display: tabIds[id],
            manager,
            triggerId: getTriggerId(tabGroupId, id),
            panelId: getPanelId(tabGroupId, id),
        });
        return acc;
    }, {}), [tabGroupId, tabIds, manager]);
    return [tabPropsMap, tabPanelPropsMap, manager];
}

export { useManagedTabs, useTabs };
//# sourceMappingURL=use-tabs.js.map
